/**
 * Catálogo de herramientas — contenido de la pantalla Herramientas.
 *
 * La spec exige que cada herramienta muestre: nombre, qué hace, por qué está
 * dentro del programa, cuándo se utiliza, tiempo típico, acceso e instrucciones
 * sencillas. El prototipo v2_2 no tenía `tiempoTipico` ni `comoSeUsa`.
 *
 * IMPORTANTE: esto es la REFERENCIA CONSULTABLE. Las instrucciones específicas
 * de la sesión de hoy viven en `uso.queHacer` de cada paso, nunca aquí.
 */

import type { Herramienta } from '../tipos.js';

export const HERRAMIENTAS: Herramienta[] = [
  {
    clave: 'chatgpt',
    nombre: 'ChatGPT Voice',
    icono: '🎙️',
    queHace:
      'Es el gimnasio principal de speaking: conversación espontánea, role-play, situaciones impredecibles, follow-ups, desacuerdo, clarificación, reformulación, repair, turn-taking y feedback.',
    porQue:
      'Te da mucho volumen de conversación impredecible y te permite practicar situaciones nuevas sin preparar respuestas. Es donde ocurre la mayor parte del entrenamiento.',
    cuando:
      'Días 1, 3, 4 y 5; parte del Día 2; respaldo del Día 6; baseline y checkpoints.',
    tiempoTipico: 'Variable: entre 12 y 38 minutos según la sesión y la duración.',
    url: 'https://chatgpt.com/',
    comoSeUsa: [
      'Copiá el prompt que la sesión te da. Nunca lo escribas de memoria: cada día tiene reglas distintas.',
      'Abrí ChatGPT, pegá el prompt y enviálo.',
      'Tocá el botón de voz para entrar en modo Voice.',
      'Hablá. No pidas correcciones durante el primer intento.',
      'Volvé a la aplicación cuando termines: tu sesión te espera en el mismo paso.',
    ],
  },
  {
    clave: 'youglish',
    nombre: 'YouGlish',
    icono: '🔊',
    queHace:
      'Buscador de ejemplos reales de una palabra, frase o patrón concreto, dichos por hablantes distintos en contextos auténticos.',
    porQue:
      'Sirve para oír reductions, linking, ritmo y uso natural en habla real. No es YouTube: no se usa para buscar temas generales ni para ver videos completos.',
    cuando:
      'Principalmente Día 2. También en el Día 5 cuando el foco es listening o naturalidad. NO se usa en baseline ni en checkpoints: ahí la comprensión se mide con una muestra auténtica desconocida.',
    tiempoTipico: '5–10 minutos.',
    url: 'https://youglish.com/',
    comoSeUsa: [
      'Elegí un target exacto: una palabra, una frase o un patrón. Nunca un tema general.',
      'Buscá esa palabra o frase exacta.',
      'Preferí ejemplos de inglés estadounidense cuando corresponda.',
      'Escuchá unos 5–10 ejemplos relevantes.',
      'Observá stress, reductions, linking, rhythm y qué palabras aparecen alrededor.',
      'Imitá 2 o 3 ejemplos útiles.',
      'Cerrá YouGlish.',
      'Producí 2 o 3 frases NUEVAS con ese patrón.',
      'Llevalo a conversación.',
    ],
  },
  {
    clave: 'muestraListening',
    nombre: 'Muestra auténtica de listening',
    icono: '📻',
    queHace:
      'Un fragmento corto de habla estadounidense real que NO conocés de antemano, para medir de verdad cuánto entendés.',
    porQue:
      'Es distinta de YouGlish y no la reemplaza. Si buscás una frase que vos elegiste, ya sabés qué vas a oír: eso mide reconocimiento, no comprensión. Para el baseline y los checkpoints el material tiene que ser desconocido, o la medición no vale.',
    cuando: 'Baseline y checkpoints de las semanas 4, 8 y 12.',
    tiempoTipico: '8–10 minutos, sobre un clip de 30 a 90 segundos.',
    url: null,
    comoSeUsa: [
      'Abrí un clip NUEVO de 30 a 90 segundos de inglés estadounidense natural.',
      'Primera escucha SIN subtítulos ni transcripción. Ni una mirada al texto.',
      'Resumí en inglés lo que entendiste.',
      'Segunda escucha si el paso lo indica, todavía sin texto.',
      'Solo DESPUÉS comprobá con subtítulos o transcripción, si están disponibles.',
      'Anotá la dificultad percibida y si el fallo fue de vocabulario o de percepción.',
      'No hay una fuente obligatoria: si una no funciona, usá otra. Lo único que importa es que el fragmento sea nuevo para vos.',
    ],
  },
  {
    clave: 'accentCoach',
    nombre: 'English Accent Coach',
    icono: '👂',
    queHace:
      'Entrenamiento perceptivo: discriminación de vocales y consonantes del inglés estadounidense.',
    porQue:
      'Entrena tu oído para distinguir sonidos antes de pedirte producirlos consistentemente. Es difícil producir con constancia una diferencia que todavía no percibís con claridad.',
    cuando:
      'Día 2, y Día 5 cuando el foco es pronunciación. Solo cuando existe un objetivo concreto.',
    tiempoTipico: '5–12 minutos.',
    url: 'https://www.englishaccentcoach.com/',
    comoSeUsa: [
      'Trabajá UN solo contraste o patrón por sesión.',
      'No navegues al azar entre ejercicios: eso no entrena nada.',
      'Buscá varios ejemplos y voces del mismo contraste, no repeticiones del mismo audio.',
      'Terminá siempre produciendo vos: percepción sin producción se queda a medias.',
      'Si no tenés todavía un objetivo fonético propio, la sesión te lo dirá y te dará una alternativa.',
    ],
  },
  {
    clave: 'grabadora',
    nombre: 'Grabadora',
    icono: '🎧',
    queHace:
      'Grabar tu voz dentro de la aplicación, escucharte, comparar tu producción y conservar muestras a lo largo del programa.',
    porQue:
      'Tu propia voz se percibe distinta mientras hablás. Escuchar la grabación después revela diferencias que pasan desapercibidas mientras producís.',
    cuando:
      'Baseline y checkpoints (muestras comparables), y bloques cortos de pronunciación.',
    tiempoTipico: '2–8 minutos.',
    url: null,
    comoSeUsa: [
      'Hay dos vías y solo dos: grabar acá dentro, o cargar un archivo de audio que ya tengas.',
      'Mientras grabás, mantené la pantalla encendida: si Android la bloquea, la grabación puede cortarse.',
      'Escuchá la grabación una vez. No la repitas cinco veces buscando defectos.',
      'Las muestras de baseline y checkpoints se pueden descargar al dispositivo, además de quedar guardadas acá.',
      'Si no diste permiso al micrófono, grabá con la app de grabadora de tu teléfono y usá «Cargar archivo».',
    ],
  },
  {
    clave: 'humano',
    nombre: 'Persona real',
    icono: '🧑‍🤝‍🧑',
    queHace: 'Conversación con una persona real, en inglés, sin que sea una clase.',
    porQue:
      'Comprueba si lo entrenado funciona con alguien que tiene intenciones, ritmo y reacciones propias y que no conoce el ejercicio. Es la prueba de transferencia real.',
    cuando: 'Día 6, idealmente una vez por semana.',
    tiempoTipico: '30–45 minutos idealmente.',
    url: null,
    comoSeUsa: [
      'No hace falta ninguna aplicación.',
      'No lo conviertas en una clase ni en un ejercicio.',
      'Concentrate en entender y reaccionar, no en sonar perfecto.',
      'Al terminar, guardá como máximo 1–3 cosas que te costaron más que hablar con la IA.',
      'Si hoy no conseguís una persona real, ChatGPT sirve de respaldo — pero no es lo mismo.',
    ],
  },
  {
    clave: 'anki',
    nombre: 'Anki / AnkiDroid',
    icono: '🗂️',
    queHace: 'Repetición espaciada automática.',
    porQue:
      'NO forma parte del núcleo del programa. Solo tiene sentido más adelante, si el Learning Bank llega a ser tan grande que revisarlo manualmente resulte incómodo.',
    cuando:
      'Opcional y más adelante. No tiene ningún día asignado y ninguna sesión lo usa.',
    tiempoTipico: 'No aplica en la V1.',
    url: 'https://apps.ankiweb.net/',
    comoSeUsa: [
      'No lo instales todavía. No lo necesitás para empezar.',
      'Si algún día lo usás, no crees barajas gigantes de vocabulario aislado: eso es justo lo que este programa evita.',
    ],
  },
  {
    clave: 'claude',
    nombre: 'Claude',
    icono: '🟠',
    queHace: 'Segunda opinión, análisis de textos, auditorías, organización.',
    porQue:
      'Puede complementar el proyecto, pero no es necesario para el núcleo de speaking y no se usa como evaluador fonético.',
    cuando: 'Opcional. No tiene día asignado ni forma parte de ninguna sesión.',
    tiempoTipico: 'No aplica.',
    url: 'https://claude.ai/',
    comoSeUsa: [
      'Usalo si querés contrastar decisiones o analizar material escrito.',
      'No lo trates como instrumento de medición de pronunciación.',
    ],
  },
];

/** Índice por clave, para búsquedas rápidas desde la interfaz. */
export const HERRAMIENTA_POR_CLAVE = Object.fromEntries(
  HERRAMIENTAS.map((h) => [h.clave, h]),
) as Record<Herramienta['clave'], Herramienta>;
